if (!customElements.get('kit-product-bundle-link')) {
  customElements.define(
    'kit-product-bundle-link',
    class KitProductBundleLink extends HTMLElement {
      connectedCallback() {
        this.onclick = async (event) => {
          event.preventDefault();
          const button = this.querySelector('button');
          const status = this.querySelector('[data-bundle-status]');
          if (button.disabled) return;
          const purchase = document.getElementById(this.dataset.purchaseId);
          const bundleInput = purchase?.querySelector('[data-formula-input][value="bundle"]');
          if (!bundleInput || typeof purchase.addToCart !== 'function') {
            status.textContent = 'La formule est indisponible. Veuillez réessayer.';
            return;
          }

          const variantSelect = purchase.querySelector('[data-complement-variant-select]');
          const option = variantSelect && Array.from(variantSelect.options).find(
            (option) => option.value === this.dataset.variantId && !option.disabled
          );
          if (option) {
            variantSelect.value = option.value;
            variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (bundleInput.disabled) {
            status.textContent = 'Cette tresse est temporairement indisponible.';
            return;
          }

          event.preventDefault();
          bundleInput.checked = true;
          bundleInput.dispatchEvent(new Event('change', { bubbles: true }));
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          status.textContent = 'Ajout au panier…';
          try {
            const added = await purchase.addToCart({ redirect: false });
            status.textContent = added
              ? 'Kit et tresse ajoutés au panier.'
              : purchase.errorElement?.textContent || "Impossible d'ajouter la formule. Veuillez réessayer.";
          } finally {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
        };
      }
    }
  );
}

if (!customElements.get('kit-product-gallery')) {
  customElements.define(
    'kit-product-gallery',
    class KitProductGallery extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
        this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        this.counter = this.querySelector('[data-gallery-current]');
        this.previousButton = this.querySelector('[data-gallery-previous]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.activeIndex = Math.max(this.items.findIndex((item) => item.classList.contains('is-active')), 0);
        this.pointerStart = null;

        this.previousButton?.addEventListener('click', () => this.select(this.activeIndex - 1));
        this.nextButton?.addEventListener('click', () => this.select(this.activeIndex + 1));
        this.thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => this.select(index));
        });
        this.querySelector('[data-gallery-stage]')?.addEventListener('pointerdown', (event) => {
          this.pointerStart = { x: event.clientX, y: event.clientY };
        });
        this.querySelector('[data-gallery-stage]')?.addEventListener('pointerup', (event) => {
          if (!this.pointerStart || this.items.length < 2) return;
          const deltaX = event.clientX - this.pointerStart.x;
          const deltaY = event.clientY - this.pointerStart.y;
          this.pointerStart = null;
          if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          this.select(deltaX < 0 ? this.activeIndex + 1 : this.activeIndex - 1);
        });
        this.querySelector('[data-gallery-stage]')?.addEventListener('pointercancel', () => {
          this.pointerStart = null;
        });

        this.addEventListener('kpt:select-media', (event) => {
          const mediaId = String(event.detail?.mediaId || '');
          const index = this.items.findIndex((item) => item.dataset.mediaId === mediaId);
          if (index >= 0) this.select(index);
        });
      }

      select(nextIndex) {
        if (!this.items.length) return;
        const index = (nextIndex + this.items.length) % this.items.length;
        this.items.forEach((item, itemIndex) => {
          const isActive = itemIndex === index;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-hidden', String(!isActive));
          if (!isActive) item.querySelector('video')?.pause();
        });
        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const isActive = thumbnailIndex === index;
          thumbnail.classList.toggle('is-active', isActive);
          thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        this.activeIndex = index;
        if (this.counter) this.counter.textContent = String(index + 1);
        this.thumbnails[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  );
}

if (!customElements.get('kit-product-purchase')) {
  customElements.define(
    'kit-product-purchase',
    class KitProductPurchase extends HTMLElement {
      connectedCallback() {
        this.variants = this.parseJson('[data-kit-variants]');
        this.complementVariants = this.parseJson('[data-complement-variants]');
        this.threshold = Number(this.dataset.threshold) || 0;
        this.currency = this.dataset.currency || 'EUR';
        this.locale = this.dataset.locale || 'fr-FR';
        this.addLabel = this.dataset.addLabel || 'Ajouter au panier';
        this.soldOutLabel = this.dataset.soldOutLabel || 'Épuisé';
        this.unavailableLabel = this.dataset.unavailableLabel || 'Indisponible';
        this.remainingTemplate = this.dataset.remainingTemplate || 'Plus que [amount] pour profiter de la livraison offerte';
        this.unlockedLabel = this.dataset.unlockedLabel || 'Livraison offerte débloquée';
        this.addComplementLabel =
          this.dataset.addComplementLabel || 'Ajoutez le complément recommandé et profitez de la livraison offerte';
        this.productPreorder = this.dataset.productPreorder === 'true';
        this.complementPreorder = this.dataset.complementPreorder === 'true';
        this.preorderLabel = this.dataset.preorderLabel || 'PRÉCOMMANDE';
        this.preorderButtonLabel = this.dataset.preorderButtonLabel || 'Précommander';
        this.preorderStockLabel = this.dataset.preorderStockLabel || 'Disponible en précommande';
        this.preorderShippingText = this.dataset.preorderShippingText || '';
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.variantSelect = this.querySelector('[data-kit-variant-select]');
        this.complementSelect = this.querySelector('[data-complement-variant-select]');
        this.formulaInputs = Array.from(this.querySelectorAll('[data-formula-input]'));
        this.formulaCards = Array.from(this.querySelectorAll('.kpt-purchase__formula'));
        this.submitButtons = Array.from(this.querySelectorAll('[data-purchase-submit]'));
        this.errorElement = this.querySelector('[data-purchase-error]');
        this.isSubmitting = false;
        this.shippingUnlocked = undefined;
        this.shippingAnimationTimer = null;

        this.variantSelect?.addEventListener('change', () => this.render());
        this.complementSelect?.addEventListener('change', () => this.render());
        this.formulaInputs.forEach((input) => input.addEventListener('change', () => this.render()));
        this.formulaCards.forEach((card) => {
          card.addEventListener('click', (event) => {
            if (event.target instanceof Element && event.target.closest('input, select, button, a')) return;
            const input = card.querySelector('[data-formula-input]');
            if (!input || input.disabled || input.checked) return;
            event.preventDefault();
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
        this.submitButtons.forEach((button) => button.addEventListener('click', () => this.addToCart()));

        this.render();
      }

      disconnectedCallback() {
        if (this.shippingAnimationTimer) window.clearTimeout(this.shippingAnimationTimer);
      }

      parseJson(selector) {
        const node = this.querySelector(selector);
        if (!node) return [];
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      selectedVariant() {
        const id = Number(this.variantSelect?.value || this.dataset.initialVariantId);
        return this.variants.find((variant) => Number(variant.id) === id) || this.variants[0];
      }

      selectedComplement() {
        const id = Number(this.complementSelect?.value || this.dataset.initialComplementVariantId);
        return (
          this.complementVariants.find((variant) => Number(variant.id) === id) ||
          this.complementVariants.find((variant) => variant.available) ||
          this.complementVariants[0]
        );
      }

      selectedFormula() {
        return this.formulaInputs.find((input) => input.checked)?.value || 'solo';
      }

      render() {
        const variant = this.selectedVariant();
        const complement = this.selectedComplement();
        let formula = this.selectedFormula();
        const complementAvailable = Boolean(complement?.available);

        const bundleInput = this.formulaInputs.find((input) => input.value === 'bundle');
        if (bundleInput) {
          bundleInput.disabled = !complementAvailable;
          bundleInput.closest('.kpt-purchase__formula')?.classList.toggle('is-disabled', !complementAvailable);
        }
        if (formula === 'bundle' && !complementAvailable) {
          formula = 'solo';
          const soloInput = this.formulaInputs.find((input) => input.value === 'solo');
          if (soloInput) soloInput.checked = true;
        }

        const productAvailable = Boolean(variant?.available);
        const formulaIncludesPreorder =
          this.productPreorder || (formula === 'bundle' && this.complementPreorder);
        const total = Number(variant?.price || 0) + (formula === 'bundle' ? Number(complement?.price || 0) : 0);
        const compareTotal =
          Number(variant?.compare_at_price || 0) +
          (formula === 'bundle' ? Number(complement?.compare_at_price || complement?.price || 0) : 0);
        const savings = compareTotal > total ? Math.round(((compareTotal - total) * 100) / compareTotal) : 0;

        this.updateText('[data-purchase-price]', this.formatMoney(total));
        this.updateText('[data-solo-price]', this.formatMoney(Number(variant?.price || 0)));
        this.updateText(
          '[data-bundle-price]',
          this.formatMoney(Number(variant?.price || 0) + Number(complement?.price || 0))
        );
        this.updateText(
          '[data-purchase-compare]',
          compareTotal > total ? this.formatMoney(compareTotal) : ''
        );
        this.querySelectorAll('[data-purchase-compare]').forEach((node) => {
          node.toggleAttribute('hidden', compareTotal <= total);
        });
        this.updateText('[data-purchase-saving]', savings > 0 ? `-${savings}%` : '');
        this.querySelectorAll('[data-purchase-saving]').forEach((node) => node.toggleAttribute('hidden', savings <= 0));

        const availabilityNodes = this.querySelectorAll('[data-purchase-availability]');
        availabilityNodes.forEach((node) => {
          node.textContent = this.productPreorder
            ? node.dataset.preorderLabel || this.preorderStockLabel
            : productAvailable
              ? node.dataset.availableLabel
              : node.dataset.soldOutLabel;
          node.classList.toggle('is-available', productAvailable && !this.productPreorder);
          node.classList.toggle('is-preorder', this.productPreorder);
        });

        const mainSection = this.closest('.kpt-main');
        if (mainSection) {
          const mainPrice = mainSection.querySelector('[data-main-purchase-price]');
          const mainCompare = mainSection.querySelector('[data-main-purchase-compare]');
          const mainSaving = mainSection.querySelector('[data-main-purchase-saving]');
          const mainAvailability = mainSection.querySelector('[data-main-purchase-availability]');
          if (mainPrice) mainPrice.textContent = this.formatMoney(total);
          if (mainCompare) {
            mainCompare.textContent = compareTotal > total ? this.formatMoney(compareTotal) : '';
            mainCompare.toggleAttribute('hidden', compareTotal <= total);
          }
          if (mainSaving) {
            mainSaving.textContent = savings > 0 ? `-${savings}%` : '';
            mainSaving.toggleAttribute('hidden', savings <= 0);
          }
          if (mainAvailability) {
            mainAvailability.textContent = this.productPreorder
              ? mainAvailability.dataset.preorderLabel || this.preorderStockLabel
              : productAvailable
                ? mainAvailability.dataset.availableLabel
                : mainAvailability.dataset.soldOutLabel;
            mainAvailability.classList.toggle('is-available', productAvailable && !this.productPreorder);
            mainAvailability.classList.toggle('is-preorder', this.productPreorder);
          }
        }

        const availableButtonLabel = formulaIncludesPreorder ? this.preorderButtonLabel : this.addLabel;
        const buttonLabel = productAvailable
          ? `${availableButtonLabel} — ${this.formatMoney(total)}`
          : this.soldOutLabel;
        this.updateText('[data-purchase-button-label]', buttonLabel);
        this.submitButtons.forEach((button) => {
          button.disabled = !productAvailable || this.isSubmitting;
          button.setAttribute('aria-disabled', String(!productAvailable || this.isSubmitting));
        });
        this.querySelectorAll('[data-purchase-preorder]').forEach((notice) => {
          notice.toggleAttribute('hidden', !formulaIncludesPreorder);
        });
        this.querySelectorAll('[data-purchase-notes]').forEach((notes) => {
          notes.toggleAttribute('hidden', formulaIncludesPreorder);
        });

        const progress = this.threshold > 0 ? Math.min((total / this.threshold) * 100, 100) : 100;
        this.style.setProperty('--kpt-progress', `${progress}%`);
        this.querySelectorAll('[data-shipping-track]').forEach((track) => {
          track.setAttribute('aria-valuenow', String(Math.min(total, this.threshold)));
          track.setAttribute(
            'aria-valuetext',
            `${this.formatMoney(total)} sur ${this.formatMoney(this.threshold)}`
          );
        });

        let shippingMessage = this.unlockedLabel;
        if (this.threshold > total) {
          const bundleTotal = Number(variant?.price || 0) + Number(complement?.price || 0);
          if (formula === 'solo' && complementAvailable && bundleTotal >= this.threshold) {
            shippingMessage = this.addComplementLabel;
          } else {
            shippingMessage = this.remainingTemplate.replace(
              '[amount]',
              this.formatMoney(this.threshold - total)
            );
          }
        }
        this.updateText('[data-shipping-message]', shippingMessage);

        const isUnlocked = total >= this.threshold;
        this.querySelectorAll('[data-shipping-container]').forEach((container) => {
          container.classList.toggle('is-unlocked', isUnlocked);
          if (this.shippingUnlocked === false && isUnlocked) {
            container.classList.remove('is-celebrating');
            requestAnimationFrame(() => {
              container.classList.add('is-celebrating');
              if (this.shippingAnimationTimer) window.clearTimeout(this.shippingAnimationTimer);
              this.shippingAnimationTimer = window.setTimeout(
                () => container.classList.remove('is-celebrating'),
                650
              );
            });
          }
        });
        this.shippingUnlocked = isUnlocked;

        if (variant?.featured_media_id) {
          this.closest('.kpt-main')
            ?.querySelector('kit-product-gallery')
            ?.dispatchEvent(
              new CustomEvent('kpt:select-media', {
                detail: { mediaId: variant.featured_media_id },
              })
            );
        }
      }

      updateText(selector, value) {
        this.querySelectorAll(selector).forEach((node) => {
          node.textContent = value;
        });
      }

      async addToCart({ redirect = true } = {}) {
        if (this.isSubmitting) return;
        const variant = this.selectedVariant();
        const complement = this.selectedComplement();
        const formula = this.selectedFormula();
        if (!variant?.available) {
          this.showError(this.soldOutLabel);
          return;
        }
        if (formula === 'bundle' && !complement?.available) {
          this.showError(this.unavailableLabel);
          return;
        }

        const items = [
          {
            id: Number(variant.id),
            quantity: 1,
            ...(this.productPreorder ? { properties: this.preorderProperties() } : {}),
          },
        ];
        if (formula === 'bundle') {
          items.push({
            id: Number(complement.id),
            quantity: 1,
            ...(this.complementPreorder ? { properties: this.preorderProperties() } : {}),
          });
        }
        this.isSubmitting = true;
        this.showError();
        this.setLoading(true);

        try {
          const payload = { items, sections: ['cart-icon-bubble'], sections_url: window.location.pathname };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement?.(document.activeElement);
          }

          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });
          const cartData = await response.json();
          if (!response.ok || cartData.status) {
            throw new Error(cartData.description || cartData.message || "Impossible d'ajouter les produits au panier.");
          }

          const addedItems = Array.isArray(cartData.items) ? cartData.items : [];
          const displayItem = addedItems[addedItems.length - 1] || addedItems[0];
          if (displayItem) {
            cartData.id = displayItem.id;
            cartData.key = displayItem.key;
          }

          if (this.cart && cartData.sections) {
            try {
              this.cart.renderContents(cartData);
            } catch (renderError) {
              console.error(renderError);
            }
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'kit-product-template',
              productVariantId: variant.id,
              cartData,
            });
          }
          if (!this.cart && cartData.sections?.['cart-icon-bubble']) {
            const icon = document.getElementById('cart-icon-bubble');
            const content = new DOMParser().parseFromString(cartData.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
            if (icon && content) icon.innerHTML = content.innerHTML;
          }
          if (!this.cart && redirect) window.location.assign(window.routes.cart_url);
          return true;
        } catch (error) {
          this.showError(error.message);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'kit-product-template',
              errors: error.message,
              message: error.message,
            });
          }
        } finally {
          this.isSubmitting = false;
          this.setLoading(false);
          this.render();
        }
      }

      setLoading(isLoading) {
        this.submitButtons.forEach((button) => {
          button.classList.toggle('loading', isLoading);
          button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
        });
      }

      showError(message = '') {
        if (!this.errorElement) return;
        this.errorElement.hidden = !message;
        this.errorElement.textContent = message;
      }

      preorderProperties() {
        const properties = {
          _preorder: 'true',
          Statut: this.preorderLabel,
        };
        if (this.preorderShippingText) {
          properties['Expédition estimée'] = this.preorderShippingText;
        }
        return properties;
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format((Number(cents) || 0) / 100);
      }
    }
  );
}
